<template>
  <BContentBox v-loading="loading">
    <template #content>
      <div class="content-container">
        <div class="section">
          <div class="title">
            {{ $t('callFeedbackSetting.talkSeconds.title') }}
          </div>
          <div class="explain">
            {{ $t('callFeedbackSetting.talkSeconds.description') }}
          </div>
          <div
            class="time-items"
          >
            <BListItem class="mb-300">
              <BInput
                v-model="talkSeconds"
                input-id="talkSeconds"
                :min="0"
                :placeholder="$t('callFeedbackSetting.talkSeconds.placeholder')"
                needs-parse-int
              />
            </BListItem>
          </div>
        </div>
        <div class="section">
          <div class="title">
            {{ $t('callFeedbackSetting.callResult.title') }}
          </div>
          <div class="explain">
            {{ $t('callFeedbackSetting.callResult.description') }}
          </div>
          <div
            class="time-items"
          >
            <BListItem class="mb-300">
              <CallResultTypeSelect
                v-model:default-call-result-type-ids="defaultCallResultTypeIds"
                v-model:custom-call-result-type-ids="callResultTypeIds"
              />
            </BListItem>
          </div>
        </div>
        <BLayout>
          <BBtn
            type="primary"
            :disabled="loading"
            @click="handleSaveClick"
          >
            {{ $t('general.save.text') }}
          </BBtn>
        </BLayout>
      </div>
    </template>
  </BContentBox>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CallFeedbackSettingApiService } from '@/api/user/resources/call_feedback_setting';
import CallResultTypeSelect from '@/components/organisms/user/callFb/CallResultTypeSelect.vue';
import { useWait } from '@/composable/vue-wait';
import { KEY_OF_TOAST } from '@/injection-keys';

const talkSeconds = ref<number>();
const defaultCallResultTypeIds = ref<number[]>([]);
const callResultTypeIds = ref<number[]>([]);

const toast = inject(KEY_OF_TOAST);
const { doActionWithWait, wait } = useWait();
const i18n = useI18n();

const loading = computed(() =>
  wait.is([
    'fetchCallFeedbackSettingWait',
    'putCallFeedbackSettingWait',
  ]),
);

const fetchCallFeedbackSetting = () => {
  doActionWithWait('fetchCallFeedbackSettingWait', async () => {
    const api = new CallFeedbackSettingApiService();
    const { data } = await api.getCallFeedbackSetting({
      errorHandlers: {
        404: () => undefined,
      },
    });
    if (data?.callFeedbackSetting) {
      talkSeconds.value = data.callFeedbackSetting.talkSeconds;
      defaultCallResultTypeIds.value = data.callFeedbackSetting.defaultCallResultTypeIds || [];
      callResultTypeIds.value = data.callFeedbackSetting.callResultTypeIds || [];
    }
  });
};

const handleSaveClick = async () => {
  doActionWithWait('putCallFeedbackSettingWait', async () => {
    const api = new CallFeedbackSettingApiService();
    await api.putCallFeedbackSetting({
      request: {
        callFeedbackSettingBody: {
          callFeedbackSetting: {
            talkSeconds: talkSeconds.value,
            defaultCallResultTypeIds: defaultCallResultTypeIds.value,
            callResultTypeIds: callResultTypeIds.value,
          },
        },
      },
    });
  });
  toast.show(
    i18n.t('general.done.withTarget', {
      action: i18n.t('general.save.text'),
      target: i18n.t('corpSettingApi.callFeedbackSetting.autoNotice'),
    }),
  );
};

onBeforeMount(() => {
  fetchCallFeedbackSetting();
});
</script>

<style lang="scss" scoped>
.content-container {
  max-width: 900px;
}
.title {
  font-size: $fontsize-400;
  font-weight: bold;
  margin-bottom: $basespace-150;
}
.explain {
  color: $textcolor-light;
  white-space: pre-wrap;
  margin-bottom: $basespace-400;
}
.time-items {
  display: flex;
  gap: 10px;
  max-width: 480px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
</style>
